import axios from 'axios';
import axiosRetry from 'axios-retry';
import qs from 'qs';

/**
 * @type {{
 *    call(*=, *, *=): *,
 *    patch(*=, *=, *=, *=): *,
 *    request(*, *, *, *=, *=): *,
 *    init(): void,
 *    baseAuth: boolean,
 *    pdf(*, *, *=): *,
 *    post(*=, *=, *=, *=): *,
 *    ax: null,
 *    get(*=, *=, *=, *=): *,
 *    delete(*=, *=, *=, *=): *,
 *    put(*=, *=, *=, *=): *
 * }}
 */
export const http = {
  ax: null,
  baseAuth: false,
  /**
   * prepare request to send
   *
   * @param method
   * @param url
   * @param data
   * @param headers
   * @param progress
   */
  request(method, url, data, headers = {}, progress = null) {
    let params = {
      url,
      data,
      method: method.toLowerCase(),
      headers,
    };

    if (this.baseAuth) {
      params.auth = {
        username: process.env.REACT_APP_AUTH_LOGIN,
        password: process.env.REACT_APP_AUTH_PASSWD,
      };
    }

    if (params.method === 'get') {
      params.params = data;
    }

    return this.call(params, headers, progress);
  },
  /**
   * call prepared request
   *
   * @param params
   * @param headers
   * @param progress
   */
  call(params, headers, progress) {
    if (progress) {
      params.onUploadProgress = function (progressEvent) {
        progress(progressEvent);
      };
    }
    return this.ax.request(params).catch(e => {
      if (e.response.status === 444) {
        window.wipMessage = e.response.data;

        if (!(window.location.href.indexOf('progress') > -1)) {
          window.location.replace('work-in-progress');
        }
      } else {
        throw e;
      }
    });
  },
  /**
   * @param url
   * @param data
   * @param headers
   * @param progress
   * @returns {*}
   */
  get(url, data = {}, headers = {}, progress = null) {
    return this.request('get', url, data, headers, progress);
  },
  /**
   * @param url
   * @param data
   * @param headers
   * @param progress
   * @returns {*}
   */
  post(url, data = {}, headers = {}, progress = null) {
    return this.request('post', url, data, headers, progress);
  },
  /**
   * @param url
   * @param data
   * @param headers
   * @param progress
   * @returns {*}
   */
  put(url, data = {}, headers = {}, progress = null) {
    return this.request('put', url, data, headers, progress);
  },
  /**
   * @param url
   * @param data
   * @param headers
   * @param progress
   * @returns {*}
   */
  patch(url, data = {}, headers = {}, progress = null) {
    return this.request('patch', url, data, headers, progress);
  },
  /**
   * @param url
   * @param data
   * @param headers
   * @param progress
   * @returns {*}
   */
  delete(url, data = {}, headers = {}, progress = null) {
    return this.request('delete', url, data, headers, progress);
  },
  /**
   * @param url
   * @param data
   * @param headers
   * @returns {*}
   */
  pdf(url, data, headers = {}) {
    return this.call(
      {
        url,
        data,
        method: 'post',
        responseType: 'arraybuffer',
      },
      headers,
    );
  },
  external(url, method, data = {}, headers = {}) {
    let params = {
      url,
      data,
      method: method.toLowerCase(),
      headers,
      transformRequest: function (params) {
        return qs.stringify(params);
      },
    };

    return this.axExternal.request(params);
  },
  isLocal() {
    return window.location.href.search('[?&]local=') !== -1;
  },
  configInternal() {
    // set default base url for all request
    const API_URL = this.isLocal()
      ? process.env.REACT_APP_LOCAL_API_URL
      : process.env.REACT_APP_API_URL;

    const language = 'pl';

    axios.defaults.baseURL = `${API_URL}/api/${language}`;

    this.ax = axios.create({
      baseURL: axios.defaults.baseURL,
    });

    this.axExternal = axios.create();

    this.baseAuth =
      process.env.hasOwnProperty('REACT_APP_AUTH_LOGIN') &&
      process.env.hasOwnProperty('REACT_APP_AUTH_PASSWD');

    this.ax.defaults.headers.common['Content-Type'] = 'application/json';
    this.ax.defaults.headers.common['Accept'] = 'application/json';

    // Intercept the request to make sure the token is injected into the header.
    // this.ax.interceptors.request.use(config => {
    //   return config;
    // });

    // Intercept the response and ...
    this.ax.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        if (!axios.isCancel(error)) {
          switch (error.response.status) {
            // case 400:
            // case 401:
            // case 409:
            // case 451:
            // case 500:
            // case 503:
            //   break;

            // case 444: {
            //   break;
            // }

            default:
              break;
          }

          return Promise.reject(error);
        }
      },
    );

    axiosRetry(this.ax, {
      retries: 3,
      retryCondition: error => {
        return axiosRetry.isNetworkOrIdempotentRequestError(error);
      },
    });
  },
  configExternal() {
    this.axExternal = axios.create();

    // Intercept the request to make sure the token is injected into the header.
    // this.axExternal.interceptors.request.use(config => {
    //   return config;
    // });

    // Intercept the response and ...
    this.axExternal.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        if (!axios.isCancel(error)) {
          if (error?.hasOwnProperty('response') && error.response?.hasOwnProperty('status')) {
            switch (error.response.status) {
              // case 400:
              // case 401:
              // case 409:
              // case 451:
              // case 500:
              // case 503:
              //   break;

              default:
                break;
            }
          }

          return Promise.reject(error);
        }
      },
    );
    axiosRetry(this.axExternal, {
      retries: 3,
      retryCondition: error => {
        return axiosRetry.isNetworkOrIdempotentRequestError(error);
      },
    });
  },
  /**
   * Init the service.
   */
  init() {
    this.configInternal();
    this.configExternal();
  },
};

http.init();

export default http;
